import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThemeToggle from "./ThemeToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "./LanguageToggle";
import Logo from "./Logo";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const navLinks = [
    { href: "#home", label: "home" },
    { href: "#about", label: "about" },
    { href: "#solutions", label: "solutions" },
    { href: "#case-studies", label: "case-studies" },
    { href: "#blog", label: "blog" },
    { href: "#contact", label: "contact" }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/70 bg-white/80 shadow-[0_10px_40px_-26px_rgba(15,23,42,0.35)] backdrop-blur-xl dark:border-slate-800/80 dark:bg-slate-950/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[4.25rem] items-center justify-between gap-3 xl:gap-5">
          {/* Logo */}
          <div className="flex min-w-0 flex-1 items-center py-1 xl:flex-none">
            <Logo size="nav" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex flex-1 items-center justify-center">
            <div className="flex items-center space-x-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                >
                  {t(link.label)}
                </a>
              ))}
            </div>
          </div>

          {/* Right side - Language Toggle, Theme Toggle, CTA */}
          <div className="flex shrink-0 items-center justify-end space-x-2 md:space-x-2.5 xl:flex-1">
            <LanguageToggle />
            <ThemeToggle />
            <Button 
              className="ml-2 hidden xl:inline-flex bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow hover:shadow-lg transition-all duration-300"
              onClick={() => {
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                window.setTimeout(() => {
                  (window as any).toast && (window as any).toast({
                    title: t("primary_cta"),
                    description: t("well_be_in_touch"),
                  });
                }, 700);
              }}
            >
              {t("primary_cta")}
            </Button>
            
            {/* Mobile menu button */}
            <div className="xl:hidden ml-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleMenu}
                className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="xl:hidden bg-white/95 dark:bg-slate-950/95 backdrop-blur-lg border-b border-slate-200/50 dark:border-slate-800/80">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block px-3 py-2 text-base font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-50 dark:hover:bg-slate-900/40 rounded-lg transition-all duration-200"
              onClick={toggleMenu}
            >
              {t(link.label)}
            </a>
          ))}
          <div className="pt-2">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow hover:shadow-lg transition-all duration-300"
              onClick={() => {
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                window.setTimeout(() => {
                  (window as any).toast && (window as any).toast({
                    title: t("primary_cta"),
                    description: t("well_be_in_touch"),
                  });
                }, 700);
              }}
            >
              {t("primary_cta")}
            </Button>
          </div>
        </div>
      </div>
      )}
    </nav>
  );
};

export default Navbar;
